import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { InactivityLogoutModal } from '@features/authentication/components/modals/InactivityLogoutModal';
import type { ModalKeyType } from '@shared/components/ui/custom/global-modal-manager/global-manager.types';
import { MODAL_KEYS } from '@shared/components/ui/custom/global-modal-manager/global-manager.types';
import { GlobalModalManager } from '@shared/components/ui/custom/global-modal-manager/GlobalModalManager';

type ModalProps = Record<string, unknown>;

interface ModalContextType {
  openModal: (key: ModalKeyType, props?: ModalProps) => void;
  closeModal: (key: ModalKeyType) => void;
  closeAllModals: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider = ({ children, sessionActive = true }: { children: ReactNode; sessionActive?: boolean }) => {
  const [activeModal, setActiveModal] = useState<ModalKeyType | null>(null);
  const [modalProps, setModalProps] = useState<ModalProps>({});
  const [inactivityOpen, setInactivityOpen] = useState(false);

  const openModal = (key: ModalKeyType, props: ModalProps = {}) => {
    if (key === MODAL_KEYS.INACTIVITY_LOGOUT) {
      setInactivityOpen(true);
      return;
    }
    setModalProps(props);
    setActiveModal(key);
  };

  const closeModal = (key: ModalKeyType) => {
    if (key === MODAL_KEYS.INACTIVITY_LOGOUT) {
      setInactivityOpen(false);
      return;
    }
    setActiveModal((current) => {
      if (key && current !== key) {
        return current;
      }
      return null;
    });
  };

  // Tears down every open modal at once. Invoked two ways: imperatively by the inactivity tick
  // (so the *initiating* tab doesn't flash a stale dialog mid-teardown), and reactively by the
  // session-end effect below (which covers every *other* logout path and tab).
  const closeAllModals = useCallback(() => {
    setActiveModal(null);
    setModalProps({});
    setInactivityOpen(false);
  }, []);

  // Without this, a logout — whether from this tab (inactivity tick / 401 refresh failure)
  // or another tab (the cross-tab LOGOUT broadcast) — would strand an open modal over the login
  // screen; worse, a modal still running a query (e.g. "new investigation" loading asset statuses)
  // would spin in a refetch -> 401 -> logout -> refetch loop. Driving teardown off the single
  // `sessionActive` signal closes every modal on every logout path in one place.
  useEffect(() => {
    if (!sessionActive) {
      closeAllModals();
    }
  }, [sessionActive, closeAllModals]);

  return (
    <ModalContext.Provider value={{ openModal, closeModal, closeAllModals }}>
      {children}
      <GlobalModalManager activeModal={activeModal} closeModal={closeModal} props={modalProps} />
      {inactivityOpen && <InactivityLogoutModal onClose={() => closeModal(MODAL_KEYS.INACTIVITY_LOGOUT)} />}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return context;
};
